import PropTypes from 'prop-types';
import React, { memo } from 'react';
import { List } from 'immutable';

import config from '../../../../config';
import getPercent from '../../../../utils/getPercent';
import { Edit } from './Edit';
import { View } from './View';

import style from './style.module.css';

const APPROVALS_STATS_SUM_COLUMNS = [
  'numberTargets',
  'numberOfContacted',
  'numberOfLeads',
  'numberOfCc',
  'numberOfVisit',
  'numberOfNextActions',
  'numberOfPriorityA',
  'numberOfPriorityB',
  'numberOfPriorityC',
  'numberOfApprovedX',
];
const TOTAL_APPROVALS_RETURNED = [
  'numberOfContacted',
  'numberOfLeads',
  'numberOfCc',
  'numberOfVisit',
  'numberOfNextActions',
  'numberOfPriorityA',
  'numberOfPriorityB',
  'numberOfPriorityC',
  'numberOfApprovedX',
];

/**
 * Return sum by field.
 *
 * @param {Immutable.List} approvals Immutable List of approvals.
 * @param {string} field Field name.
 */
const getSum = (approvals, field) =>
  approvals.reduce((sum, approval) => {
    const approvalField = approval.get(field) || 0;

    return sum + approvalField;
  }, 0);

/**
 * Table rows component.
 *
 * @param props {Object}.
 * @param props.approvals {Immutable.Map} Approval list information.
 * @param props.onChange {Function} Function to handle changing row information.
 * @param props.onClick {Function} Function to handle event of clicking on row.
 * @param props.onDoubleClick {Function} Function to handle event of double clicking on row.
 */
const RowsComponent = ({
  approvals,
  onChange,
  onClick,
  onDoubleClick,
  contextId,
  noApprovals,
  columns,
  onNoApprovalDoubleClick,
}) => {
  const type = config.values.getIn(['project', 'types', 'APPROVALS']);

  const sumNoApprovalReturnedTargets = getSum(noApprovals, 'numberTargets');
  const sumNoApproved = getSum(noApprovals, 'numberApproved');

  const cellsNoApproval = columns.map((column, i) => {
    // it needs to prevent render unnecessary td
    if (i === 2) return null;

    if (column.get('field') === 'applistLabel') {
      return (
        <td key={column.get('field')} colSpan="2">
          (No Approval List)
        </td>
      );
    }

    if (column.get('field') === 'numberTargets') {
      return (
        <td key={column.get('field')} className="text-right">
          {sumNoApprovalReturnedTargets}
        </td>
      );
    }

    if (column.get('field') === 'numberApproved') {
      const sum = getSum(noApprovals, column.get('field'));

      return (
        <td key={column.get('field')} className="text-right">
          {sum} ({getPercent(sum, sumNoApprovalReturnedTargets)}%)
        </td>
      );
    }

    if (TOTAL_APPROVALS_RETURNED.includes(column.get('field'))) {
      const sum = getSum(noApprovals, column.get('field'));
      const centered = ['numberOfPriorityA', 'numberOfPriorityB', 'numberOfPriorityC', 'numberOfApprovedX'];
      const cls = centered.includes(column.get('field')) ? 'text-center' : 'text-right';

      return (
        <td key={column.get('field')} className={cls}>
          {sum} ({getPercent(sum, sumNoApproved)}%)
        </td>
      );
    }

    if (APPROVALS_STATS_SUM_COLUMNS.includes(column.get('field'))) {
      return <td key={column.get('field')}>{getSum(noApprovals, column.get('field'))}</td>;
    }

    return <td key={column.get('field')} />;
  });

  const renderCellsNoApprovals = noApprovals.size > 0 && (
    <tr className={style.noApprovalRow} onDoubleClick={onNoApprovalDoubleClick}>
      {cellsNoApproval}
    </tr>
  );

  return (
    <tbody>
      {approvals.map(approval => {
        if (approval.get('mode') === config.EDIT_MODE) {
          return <Edit key={approval.get('id')} onChange={onChange} row={approval} />;
        }

        return (
          <View
            key={approval.get('id')}
            contextId={contextId}
            onClick={onClick}
            onCollect={() => ({ type, approval })}
            onDoubleClick={onDoubleClick}
            row={approval}
          />
        );
      })}
      {renderCellsNoApprovals}
    </tbody>
  );
};

RowsComponent.propTypes = {
  approvals: PropTypes.instanceOf(List).isRequired,
  onChange: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
  onDoubleClick: PropTypes.func.isRequired,
};

export const Rows = memo(RowsComponent);
